import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

const randInt = (min, max, nonZero = false) => {
  let value;
  do {
    value = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (nonZero && value === 0);
  return value;
};

const pick = (array) => array[Math.floor(Math.random() * array.length)];

function vectorLatex(p, q) {
  return `\\left[${p},\\ ${q}\\right]`;
}

function insideLatex(p) {
  if (p === 0) return "x";
  return p > 0 ? `x-${p}` : `x+${Math.abs(p)}`;
}

function outsideLatex(q) {
  if (q === 0) return "";
  return q > 0 ? `+${q}` : `-${Math.abs(q)}`;
}

function formulaLatex(p, q) {
  return `g(x)=f(${insideLatex(p)})${outsideLatex(q)}`;
}

function normalizeNumber(value) {
  const raw = typeof value === "object" ? value?.raw ?? "" : value ?? "";
  const normalized = String(raw)
    .replace(/\u2212/g, "-")
    .replace(/\s+/g, "")
    .replace(/^=/, "");
  if (!/^[-+]?\d+$/.test(normalized)) return null;
  return Number(normalized);
}

function makeVector(variant) {
  if (variant === "simple") {
    return Math.random() < 0.5
      ? { p: randInt(-5, 5, true), q: 0 }
      : { p: 0, q: randInt(-5, 5, true) };
  }

  return { p: randInt(-5, 5, true), q: randInt(-5, 5, true) };
}

export function generateTask(forceVariant) {
  const variant = forceVariant || pick(["simple", "mixed"]);
  const { p, q } = makeVector(variant);
  return {
    type: "ShiftVectorFromFormulaTask",
    data: { variant, p, q },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6, lineHeight: 1.6 }}>
      <p>
        Wykres funkcji <InlineMath math={"f"} /> przesunięto i otrzymano wykres funkcji <InlineMath math={"g"} />:
      </p>
      <BlockMath math={formulaLatex(data.p, data.q)} />
      <p>Odczytaj wektor przesunięcia.</p>
    </div>
  );
}

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const ui = userInput && typeof userInput === "object" ? userInput : {};
  const setField = (field) => (value) => onChange({ ...ui, [field]: value });

  return (
    <div className="flex items-center gap-2" style={{ marginTop: 8, flexWrap: "wrap" }}>
      <InlineMath math={"\\vec{v}=\\left["} />
      <div style={{ width: 92 }}>
        <EquationInput
          value={ui.p ?? ""}
          onChange={setField("p")}
          disabled={disabled}
          showPreview={false}
          placeholder="np. 3"
        />
      </div>
      <InlineMath math={", "} />
      <div style={{ width: 92 }}>
        <EquationInput
          value={ui.q ?? ""}
          onChange={setField("q")}
          disabled={disabled}
          showPreview={false}
          placeholder="np. -2"
        />
      </div>
      <InlineMath math={"\\right]"} />
    </div>
  );
}

export function checkAnswer({ data }, userInput = {}) {
  return normalizeNumber(userInput.p) === data.p && normalizeNumber(userInput.q) === data.q;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`\\vec{v}=${vectorLatex(data.p, data.q)}`} />;
}

export function renderUserAnswer(arg1, arg2) {
  const userInput = arg1?.type === "ShiftVectorFromFormulaTask" ? arg2 : arg1;
  const p = normalizeNumber(userInput?.p);
  const q = normalizeNumber(userInput?.q);
  if (p === null && q === null) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\vec{v}=\\left[${p ?? "?"},\\ ${q ?? "?"}\\right]`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ lineHeight: 1.7 }}>
      <p>
        Zapis <InlineMath math={`f(${insideLatex(data.p)})`} /> mówi o przesunięciu poziomym. W argumencie znak jest przeciwny:{" "}
        <InlineMath math={`x-${data.p}`} /> oznacza zmianę współrzędnej <InlineMath math={"x"} /> o{" "}
        <InlineMath math={String(data.p)} />.
      </p>
      <p>
        Dopisek <InlineMath math={outsideLatex(data.q) || "+0"} /> za funkcją zmienia wartości, więc daje składową pionową{" "}
        <InlineMath math={String(data.q)} />.
      </p>
      <BlockMath math={`\\vec{v}=${vectorLatex(data.p, data.q)}`} />
    </div>
  );
}
